import { injectable } from 'inversify';

import { Calendar } from '@vk-hr-tek/core/calendar';
import { FormatService } from '@vk-hr-tek/core/format';

import {
  UpdateVacationScheduleRequest,
  VacationSchedule,
} from '@app/gen/vacations';

import { EditVacationDeadlinesDto } from '../../dto';

import { VacationDetailMapper } from './vacation.detail.mapper';

@injectable()
export class VacationEditDeadlinesMapper {
  constructor(
    private calendar: Calendar,
    private format: FormatService,
    private detailMapper: VacationDetailMapper,
  ) {}

  private isDeadlinePassed(deadline: string): boolean {
    const today = this.calendar.startOfDay(new Date());
    const deadlineDate = this.calendar.startOfDay(new Date(deadline));
    return this.calendar.isMoreThan(today, deadlineDate);
  }

  private processApproverDeadlines(
    approvers?: VacationSchedule['approvers'],
    editApprovers?: EditVacationDeadlinesDto['approvers'],
  ) {
    return approvers?.reduce<VacationSchedule['approvers']>(
      (acc, approver, index) => {
        const deadline = editApprovers?.[index]?.deadline;

        if (deadline && approver.deadline !== deadline) {
          acc.push({ ...approver, deadline });
        }

        return acc;
      },
      [],
    );
  }

  processVacationSchedule(schedule: VacationSchedule) {
    const { approvers, ...detail } =
      this.detailMapper.processVacationSchedule(schedule);

    return {
      ...detail,
      activeTo: schedule.active_to,
      activeToDate: this.calendar.parseDate(schedule.active_to),
      activeToFormatted: this.format.toDate(schedule.active_to),
      activeToMaxDate: this.calendar.endOfYear(new Date().getFullYear()),
      activeToDisabled: this.isDeadlinePassed(schedule.active_to),

      employeeDeadline: schedule.employee_deadline,
      employeeDeadlineDate: this.calendar.parseDate(schedule.employee_deadline),
      employeeDeadlineFormatted: this.format.toDate(schedule.employee_deadline),
      employeeDeadlineDisabled: this.isDeadlinePassed(
        schedule.employee_deadline,
      ),

      approvers: approvers.map((approver) => ({
        ...approver,
        deadlineDate: this.calendar.parseDate(approver.deadline),
        deadlineDisabled: this.isDeadlinePassed(approver.deadline),
      })),
    };
  }

  processUpdateVacationSchedule(
    schedule: VacationSchedule | null,
    body: EditVacationDeadlinesDto,
  ): UpdateVacationScheduleRequest {
    const thresholdPercent =
      body.hasThresholdPercent === 'true'
        ? Number(body.thresholdPercent) || null
        : null;
    const customName = body.customName ?? '';

    return {
      approver_deadlines: this.processApproverDeadlines(
        schedule?.approvers,
        body.approvers,
      ),
      ...(body.activeTo &&
        body.activeTo !== schedule?.active_to && {
          active_to: body.activeTo,
        }),
      ...(body.employeeDeadline &&
        body.employeeDeadline !== schedule?.employee_deadline && {
          employee_deadline: body.employeeDeadline,
        }),
      ...(thresholdPercent !== (schedule?.threshold_percent ?? null) && {
        threshold_percent: thresholdPercent as number,
      }),
      ...(customName !== (schedule?.custom_name ?? '') && {
        custom_name: customName,
      }),
    };
  }
}
